import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import api, { ApiResponse } from "../../services";
import { useAuth } from "../../context/AuthContext";
import { Order } from "../../types/Order";
import { PaginationInfo } from "../../types/PaginationInfo";
import VideoLoader from "../../components/VideoLoader";
import LeftArrow from "../../assests/left-arrow.svg";
import { ChevronDown, ChevronUp, Info, Star } from "lucide-react";
import { Plus, Minus } from "lucide-react";
import { formatScheduledDateForDisplay } from "../../lib/utils";
import FoodRatingModal from "../../components/FoodRatingModal";
import ErrorHandlerPage from "../../components/ErrorHandlerPage";

const Orderhistory: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [expandedOrders, setExpandedOrders] = useState<Record<string, boolean>>(
    {},
  );
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const { user, currencySymbol } = useAuth();
  const [activeFilter, setActiveFilter] = useState("all");
  const [isExpanded, setIsExpanded] = useState(false);
  const [showPriceTooltip, setShowPriceTooltip] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  const [ratingItem, setRatingItem] = useState<{
    item: { itemId: string; name: string };
    orderId: string;
  } | null>(null);
  const filterButtons = [
    { id: "all", label: "All Orders" },
    { id: "week", label: "Last Week Orders" },
    { id: "month", label: "Last Month Orders" },
  ];
  const filteredOrders = useMemo(() => {
    const now = new Date();

    if (activeFilter === "week") {
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(now.getDate() - 7);

      return orders.filter((order) => new Date(order.createdAt) >= oneWeekAgo);
    }

    if (activeFilter === "month") {
      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(now.getMonth() - 1);

      return orders.filter((order) => new Date(order.createdAt) >= oneMonthAgo);
    }

    return orders; // ALL
  }, [activeFilter, orders]);

  const observerTarget = useRef<HTMLDivElement>(null);
  const getOrderTaxTotal = (order: Order) => {
    return order.items.reduce((sum, item) => {
      const quantity = Number(item.quantity) || 0;
      const itemTax = Number(item.tax) || 0;
      return sum + itemTax * quantity;
    }, 0);
  };

  const MyOrders = async (userId: string, pageNum: number, append = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const response = await api.get<
        ApiResponse<Order[]> & { pagination: PaginationInfo }
      >(`/orders/myorders/?page=${pageNum}&limit=10`);

      if (response.data.success) {
        const newOrders = response.data.data;
        setOrders((prev) => (append ? [...prev, ...newOrders] : newOrders));
        setHasMore(response.data.pagination.hasMore);
      }
    } catch {
      setError("Order history is currently unavailable.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };
  useEffect(() => {
    if (user?.id) {
      MyOrders(user.id, 1, false);
    }
    if(!user){
      navigate('/');
    }
  }, [user]);

  const handleLoadMore = () => {
    if (user?.id && hasMore && !loadingMore) {
      setPage((prev) => prev + 1);
      MyOrders(user.id, page + 1, true);
    }
  };
  const toggleOrderExpansion = (orderId: string) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  useEffect(() => {
    if (showOrderModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [showOrderModal]);

  const OrderDetailsModal = ({ order }: { order: Order }) => {
    const [expandedItems, setExpandedItems] = useState<Record<number, boolean>>(
      {},
    );
    const isItemExpanded = (index: number) => expandedItems[index];
    // const totalTax = getOrderTaxTotal(order);
    if (!order) return null;

    return (
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={() => setShowOrderModal(false)}
      >
        <div
          className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto no-scrollbar"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="sticky top-0 bg-white border-b border-black/20 p-4 flex justify-between items-center rounded-t-2xl">
            <h2 className="text-[22px] font-bold text-black">
              Order #{order.orderNumber}
            </h2>
            <button
              onClick={() => setShowOrderModal(false)}
              className="dm-order-close w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition"
            >
              ✕
            </button>
          </div>

          {/* Modal Content */}
          <div className="p-6">
            {/* Date Info */}
            <div className="mb-4 pb-4 border-b border-black/20">
              <p className="text-[14px] text-black/60 mb-1">
                Order Placed:{" "}
                {new Date(order.createdAt).toLocaleString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </p>
              {order.updatedAt && (
                <p className="text-[14px] text-black/60">
                  Delivered:{" "}
                  {new Date(order.updatedAt).toLocaleString(undefined, {
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              )}
              {order.scheduledAt && order.orderStatus == "scheduled" && (
                <p className="text-[14px] text-black/60">
                  Schedsuled:{" "}
                  {order.scheduledTimezone
                    ? formatScheduledDateForDisplay(
                        order.scheduledAt.toString(),
                        order.scheduledTimezone,
                      ).full
                    : new Date(order.scheduledAt).toLocaleString(undefined, {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                </p>
              )}
            </div>

            {/* All Items */}
            <div className="space-y-3 mb-4">
              <h3 className="font-bold text-[16px] text-black">Order Items</h3>
              {order.items.map((item, i) => (
                <div key={i} className="bg-white rounded-lg p-3">
                  {/* Item Header */}
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <span
                        className={`font-semibold text-[16px] ${
                          item.itemstatus === "cancelled"
                            ? "line-through text-gray-600"
                            : "text-black"
                        }`}
                      >
                        {item.name}
                      </span>
                      <span
                        className={`ml-2 text-[14px] ${
                          item.itemstatus === "cancelled"
                            ? "text-gray-600"
                            : "text-black/60"
                        }`}
                      >
                        ×{item.quantity}
                      </span>
                    </div>
                    <div className="flex gap-0.5 flex-col">
                      <div
                        className={`text-[15px] font-semibold ${
                          item.itemstatus === "cancelled"
                            ? "line-through text-gray-600"
                            : "text-black"
                        }`}
                      >
                        {currencySymbol}{(item.price * item.quantity).toFixed(2)}
                      </div>

                      <div
                        className={`text-[14px] font-medium ${
                          item.itemstatus === "cancelled"
                            ? "line-through text-gray-600"
                            : "text-black"
                        }`}
                      >
                        +{currencySymbol}{(
                          (Number(item.tax) || 0) * (Number(item.quantity) || 0)
                        ).toFixed(2)}
                      </div>
                    </div>
                  </div>

                  {/* Custom Options */}
                  {item.customOptions?.length ? (
                    <div className="ml-2 mt-2 space-y-1">
                      {item.customOptions.map((opt, j) => (
                        <p className="text-[13px] text-black/60 mb-0" key={j}>
                          • {opt.label} ({opt.qty}) +{currencySymbol}{opt.price.toFixed(2)}
                        </p>
                      ))}
                    </div>
                  ) : null}

                  {/* Rate Button */}
                  <button
                    onClick={() =>
                      setRatingItem({
                        item: { itemId: item.itemId, name: item.name },
                        orderId: order._id,
                      })
                    }
                    className="mt-2 flex items-center gap-1 px-3 py-1.5 text-[12px] font-medium text-yellow-600 border border-yellow-400 rounded-full hover:bg-yellow-50 transition"
                  >
                    <Star size={13} className="fill-yellow-400 text-yellow-400" />
                    Rate this item
                  </button>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="border-t border-black/20 pt-4">
              <div className="flex flex-col gap-2">
                {/* <div className="flex justify-between items-center">
                  <span className="text-[15px] text-black/70">Tax:</span>
                  <span className="text-[15px] font-semibold text-black">
                    +${totalTax.toFixed(2)}
                  </span>
                </div> */}
                <div className="flex justify-between items-center">
                  <span className="text-[15px] text-black/70">
                    Delivery Charges:
                  </span>
                  <span className="text-[15px] font-semibold text-black">
                    +{currencySymbol}{(order.deliveryAmount ?? 0).toFixed(2)}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-[15px] text-black/70">Discount:</span>
                  <span className="font-semibold text-[15px] text-black">
                    -{currencySymbol}{(order.discountAmount ?? 0).toFixed(2)}
                  </span>
                </div>

                <div className="flex justify-between items-center mt-2 pt-3 border-t border-black/30">
                  <span className="font-bold text-[20px] text-black">
                    Total:
                  </span>
                  <p className="text-[22px] font-bold text-black mb-0">
                    {order.totalAmount === 0 ? (
                      <span className="text-black font-semibold">
                        Cancelled
                      </span>
                    ) : (
                      `${currencySymbol}${(order.totalAmount ?? 0).toFixed(2)}`
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <ErrorHandlerPage
        title="Order History Unavailable"
        description="We are unable to load your order history at the moment."
        retryLabel="Reload"
        onRetry={() => {
          setError(null);
          setPage(1);
          if (user?.id) {
            MyOrders(user.id, 1, false);
          }
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Header */}

      <div className="bg-white px-6 py-4 ">
        <div className="max-w-4xl mx-auto py-0 ">
          <div className="flex items-center gap-4 px-2">
            <button
              className="p-2 rounded-full bg-transparent items-center flex justify-center w-[35px] h-[35px] border-[1px] border-black/20  text-black bg-white text-lg font-bold  transition"
              onClick={() => navigate(-1)}
            >
              {/* ← */}
              <img
                src={LeftArrow}
                alt="Back"
                className="w-4 h-4 dm-icon-invert"
              />
            </button>
            <span className="text-[19px] font-bold text-black">
              {" "}
              Order History
            </span>
          </div>
        </div>
      </div>
      <div className=" text-black sm:p-6  ">
        {/* <div className="flex gap-2 mb-6 justify-center flex-wrap ">
  {filterButtons.map((filter) => (
    <button
      key={filter.id}
      onClick={() => setActiveFilter(filter.id)}
      className={`px-6 py-2.5 rounded-full text-[15px] sm:text-[17px] font-semibold transition-all duration-300 ${
        activeFilter === filter.id
          ? 'bg-[rgba(219,0,7,1)] text-white shadow-lg scale-105'
          : 'bg-white text-black/70 border-2 border-black/20 hover:border-[rgba(219,0,7,1)] hover:text-[rgba(219,0,7,1)]'
      }`}
    >
      {filter.label}
    </button>
  ))}
</div> */}
        {/* Orders Section */}
        {loading ? (
          <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
            <VideoLoader size="medium" />
          </div>
        ) : orders.length === 0 ? (
          <p className="text-center text-black/60 mt-10 text-lg font-medium">
            No orders found.
          </p>
        ) : (
          <div className="pb-20">
            <div className="max-w-4xl mx-auto px-2 sm:px-6 space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 px-4">
                {filteredOrders.map((order, index, orderIndex) => {
                  const displayItems = order.items.slice(0, 3);
                  const hasMoreItems = order.items.length > 3;
                  // const totalTax = getOrderTaxTotal(order);

                  return (
                    <div
                      key={order._id}
                      className="rounded-2xl border-[1px] border-black/20 bg-white p-4 "
                      // hover:-translate-y-1
                    >
                      {/* Header Info */}
                      <div className="flex flex-row sm:justify-between sm:items-start gap-2 items-start mb-2">
                        <div className="flex-1">
                          <h3 className="text-black text-[18px] sm:text-[20px] font-bold ">
                            Order #{order.orderNumber}
                          </h3>
                         
                        </div>
                        
                        {order.updatedAt && (
                          // text-black/70
                          <p
                            className={`text-[13px] sm:text-[14px] sm:text-right  font-normal mb-0 dark:text-white ${
                              order.deliveryStatus === "canceled"
                                ? "text-black"
                                : order.deliveryStatus === "delivered"
                                  ? "text-green-500"
                                  : "text-black/70"
                            }`}
                          >
                            {order.deliveryStatus === "canceled"
                              ? "Cancelled"
                              : order.deliveryStatus === "delivered"
                                ? "Delivered"
                                : order.deliveryStatus
                                    .replace(/_/g, " ")
                                    .charAt(0)
                                    .toUpperCase() +
                                  order.deliveryStatus
                                    .replace(/_/g, " ")
                                    .slice(1)}
                            {order.updatedAt && (
                              <>
                                {" "}
                                {new Date(order.updatedAt).toLocaleString(
                                  undefined,
                                  {
                                    month: "short",
                                    day: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  },
                                )}
                              </>
                            )}
                          </p>
                        )}
                      </div>
                      <div>
 <p className="text-[12px] sm:text-[13px] text-gray-600 mb-0">
                            {order.metadata?.pickup_name
                              ? `${order.metadata.pickup_name}${order.metadata?.pickup_address ? `, ${order.metadata.pickup_address.split(",").slice(-1).join(",").trim()}` : ""}`
                              : ""}
                            {/* {order.choice && (
                      <p className="text-sm font-semibold text-black/70 mb-0">
                        {order.choice}
                      </p>
                    )} */}
                          </p>
                          {order.scheduledAt &&
                            order.orderStatus === "scheduled" && (
                              <p className="text-[12px] sm:text-sm text-black/70">
                                Scheduled:{" "}
                                {order.scheduledTimezone
                                  ? formatScheduledDateForDisplay(
                                      order.scheduledAt.toString(),
                                      order.scheduledTimezone,
                                    ).full
                                  : new Date(order.scheduledAt).toLocaleString(
                                      undefined,
                                      {
                                        month: "short",
                                        day: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      },
                                    )}
                              </p>
                            )}
                      </div>

                      {/* Items */}
                      <div className="space-y-2 bg-white rounded-lg py-2 sm:py-2.5 mb-3 relative">
                        {/* {order.items.map((item, i) => ( */}
                        {displayItems.map((item, i, itemIndex) => {
                          const basePrice = item.price * item.quantity;
                          const taxAmount = (item.tax || 0) * item.quantity;
                          const totalPrice = basePrice + taxAmount;
                          const tooltipKey = `order-${order.orderNumber}-item-${itemIndex}-${item.name}`;
                          return (
                            <div
                              key={i}
                              className="flex flex-row justify-between gap-2 sm:gap-0 border-b border-black/30 pb-2 last:border-0"
                            >
                              <div className="flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span
                                    className={`font-semibold text-[14px] sm:text-base ${
                                      item.itemstatus === "cancelled"
                                        ? "line-through text-gray-600"
                                        : "text-black"
                                    }`}
                                  >
                                    {item.name}
                                  </span>
                                  <span
                                    className={`ml-2 text-sm ${
                                      item.itemstatus === "cancelled"
                                        ? "text-gray-600"
                                        : "text-black/60"
                                    }`}
                                  >
                                    ×{item.quantity}
                                  </span>
                                </div>

                                {isExpanded && item.customOptions?.length ? (
                                  <div className="ml-0 sm:ml-4 mt-1 text-[12px] sm:text-sm text-black/70 max-h-[80px] sm:max-h-[100px]overflow-y-auto no-scrollbar">
                                    {item.customOptions.map((opt, j) => (
                                      <p className="mb-0" key={j}>
                                        {opt.label} ({opt.qty}) +$
                                        {opt.price.toFixed(2)}
                                      </p>
                                    ))}
                                  </div>
                                ) : null}
                              </div>

                              {/* <div
                        className={`text-sm font-semibold ${
                          item.itemstatus === "cancelled"
                            ? "line-through text-black/70"
                            : "text-black"
                        }`}
                      >
                        ${(item.price * item.quantity).toFixed(2)}
                      </div> */}
                              {/* Price with Info Icon and Tooltip */}
                              <div className="flex items-center gap-2 self-start sm:self-auto">
                                <button
                                  onClick={() =>
                                    setRatingItem({
                                      item: { itemId: item.itemId, name: item.name },
                                      orderId: order._id,
                                    })
                                  }
                                  className="flex items-center gap-1 px-2 py-1 text-[11px] sm:text-[12px] font-medium text-yellow-600 border border-yellow-400 rounded-full hover:bg-yellow-50 transition"
                                >
                                  <Star size={12} className="fill-yellow-400 text-yellow-400" />
                                  Rate
                                </button>
                              <div
                                className="relative flex items-center gap-1"
                                onMouseEnter={() =>
                                  setActiveTooltip(tooltipKey)
                                }
                                onMouseLeave={() => setActiveTooltip(null)}
                              >
                                <span className="font-medium text-black text-[14px] sm:text-sm min-w-[50px] text-right">
                                  {currencySymbol}{totalPrice.toFixed(2)}
                                </span>
                                <Info className="w-3.5 h-3.5 text-gray-600 cursor-help" />

                                {/* Tooltip */}
                                {activeTooltip === tooltipKey && (
                                  <div className="absolute z-10 bottom-full right-0 mb-2  w-48 sm:w-56 bg-white border border-black/30  text-black dark:text-black dark:bg-white  rounded-lg text-[12px] sm:text-sm  p-2.5 sm:p-3">
                                    <div className="flex justify-between mb-1">
                                      <span>Item total</span>
                                      <span>{currencySymbol}{basePrice.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between mb-1">
                                      <span>Taxes & charges</span>
                                      <span>{currencySymbol}{taxAmount.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between border-t border-black/30 pt-1 mt-1 font-semibold">
                                      <span>Total</span>
                                      <span>{currencySymbol}{totalPrice.toFixed(2)}</span>
                                    </div>

                                    {/* Triangle pointer */}
                                    <div className="absolute top-full right-4 w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-white"></div>
                                  </div>
                                )}
                              </div>
                              </div>
                              {/* <div
                        className={`text-sm font-semibold ${
                          item.itemstatus === "cancelled"
                            ? "line-through text-black/70"
                            : "text-black"
                        }`}
                      >
                        ${(item.tax || 0).toFixed(2)}
                      </div> */}
                            </div>
                          );
                        })}
                      </div>

                      {hasMoreItems && (
                        <button
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowOrderModal(true);
                          }}
                          className="mb-3 w-full flex items-center justify-center dm-white-btn gap-2 px-3 sm:px-4 py-2 sm:py-2.5 border-[1px] text-black border-black/30  rounded-full font-semibold "
                        >
                          <ChevronDown
                            size={16}
                            className="sm:w-[18px] sm:h-[18px]"
                          />
                          Show More ({order.items.length - 2} more items)
                        </button>
                      )}

                      {/* Summary */}
                      <div>
                       <div className="space-y-2.5 relative text-gray-600">
                      {/* Tax
                      {(totalTax ?? 0) > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-[13px] sm:text-[14px]">Tax:</span>
                          <span className="text-[13px] sm:text-[14px] font-bold">
                            +${totalTax.toFixed(2)}
                          </span>
                        </div>
                      )} */}
                      {/* Delivery Charges */}
                      {(order.deliveryAmount ?? 0) > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-[13px] sm:text-[14px]">
                            Delivery Charges:
                          </span>
                          <span className="text-[13px] sm:text-[14px] font-bold">
                            +{currencySymbol}{order.deliveryAmount.toFixed(2)}
                          </span>
                        </div>
                      )}

                      {/* Tip */}
                      {(order.tipAmount ?? 0) > 0 && (
                        <div className="flex justify-between">
                          <span className="text-[13px] sm:text-[14px]">Tip:</span>
                          <span className="font-bold text-[13px] sm:text-[14px] text-black">
                            +{currencySymbol}{Number(order.tipAmount).toFixed(2)}
                          </span>
                        </div>
                      )}

                      {/* Discount */}
                      {(order.discountAmount ?? 0) > 0 && (
                        <div className="flex justify-between">
                          <span className="text-[13px] sm:text-[14px]">Discount:</span>
                          <span className="font-bold text-[13px] sm:text-[14px] text-black">
                            -{currencySymbol}{order.discountAmount.toFixed(2)}
                          </span>
                        </div>
                      )}

                      {/* Total */}
                      <div className="flex justify-between items-center pt-3">
                        <span className="font-bold text-[17px] sm:text-[19px] text-black">
                          Total:
                        </span>
                        <p className="text-[17px] sm:text-[19px] font-bold text-black mb-0">
                          {order.totalAmount === 0 ? (
                            <span className="text-red-500 font-semibold">Cancelled</span>
                          ) : (
                            `${currencySymbol}${(order.totalAmount ?? 0).toFixed(2)}`
                          )}
                        </p>
                      </div>

                    </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Loading More Indicator */}
            {loadingMore && (
              <div className="mt-8 py-4 flex flex-col items-center text-black/70 animate-pulse">
                <div className="flex items-center justify-center w-full">
                  <VideoLoader size="medium" />
                </div>
                <p className="mt-2">Loading more orders...</p>
              </div>
            )}
            {/* Observer Target - Positioned absolutely to avoid layout issues */}
            {/* {hasMore && !loadingMore && (
            <div
              ref={observerTarget}
              className="w-full h-20 flex items-center justify-center"
            >
              <div className="text-black/70 text-sm">Scroll for more</div>
            </div>
          )} */}
            {/* Load More Button */}
            {hasMore && !loadingMore && (
              <div className="flex justify-center mt-8">
                <button
                  onClick={handleLoadMore}
                  className="px-8 py-3 bg-main/90 text-white rounded-full dm-white-btn font-semibold text-[15px] sm:text-[16px]"
                >
                  Load More Orders
                </button>
              </div>
            )}

            {/* End of Results */}
            {!hasMore && orders.length > 0 && (
              <div className="text-center text-gray-600 mt-8 py-4 text-sm border-t border-black/20">
                🎉 These are the orders you’ve placed with us so far. KEEP
                ORDERING!
              </div>
            )}
          </div>
        )}
      </div>
      {showOrderModal && selectedOrder && (
        <OrderDetailsModal order={selectedOrder} />
      )}
      {ratingItem && (
        <FoodRatingModal
          item={ratingItem.item}
          orderId={ratingItem.orderId}
          onClose={() => setRatingItem(null)}
          onSubmitted={() => setRatingItem(null)}
          userId={user?.id}
        />
      )}
    </div>
  );
};

export default Orderhistory;
